import React, { InputHTMLAttributes, forwardRef } from 'react';
import classNames from 'classnames';
import IconSVG from './IconSVG';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string;
  error?: string;
  helperText?: string;
  prefix?: string | React.ReactNode;
  suffix?: string | React.ReactNode;
  iconStart?: string;
  iconEnd?: string;
  onIconEndClick?: () => void;
  onIconStartClick?: () => void;
  startAdornment?: React.ReactNode; // Alias for prefix for compatibility
  endAdornment?: React.ReactNode; // Alias for suffix for compatibility
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  testId?: string;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      error,
      helperText,
      prefix,
      suffix,
      iconStart,
      iconEnd,
      onIconEndClick,
      onIconStartClick,
      startAdornment,
      endAdornment,
      className,
      fullWidth = false,
      variant = 'outlined',
      disabled,
      testId,
      ...inputProps
    },
    ref
  ) => {
    const inputClasses = classNames(
      'block px-3 py-2 w-full focus:outline-none',
      {
        // Variant specific styles
        'border rounded-md bg-white dark:bg-gray-800': variant === 'outlined',
        'bg-gray-100 dark:bg-gray-700 border-0 rounded-md': variant === 'filled',
        'border-0 border-b border-gray-300 dark:border-gray-600 bg-transparent p-1 px-0': variant === 'standard',
        
        // Error styles
        'border-red-500 focus:ring-red-500 focus:border-red-500': error && variant !== 'standard',
        'border-b-red-500': error && variant === 'standard',
        
        // Focused styles 
        'focus:ring-2 focus:ring-blue-500 focus:border-blue-500': !error && (variant === 'outlined' || variant === 'filled'),
        'focus:border-b-blue-500 focus:border-b-2': !error && variant === 'standard',
        
        // Disabled styles
        'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-700': disabled,
        
        // Different padding when icons are present
        'pl-10': iconStart,
        'pr-10': iconEnd,
      },
      className
    );

    const containerClasses = classNames('relative', {
      'w-full': fullWidth,
      'inline-block': !fullWidth,
    });

    return (
      <div className={containerClasses} data-testid={testId}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
          </label>
        )}
        
        <div className="relative">
          {iconStart && (
            <div 
              className={classNames(
                "absolute inset-y-0 left-0 flex items-center pl-3", 
                { "cursor-pointer": !!onIconStartClick }
              )}
              onClick={onIconStartClick}
            >
              <IconSVG 
                icon={iconStart} 
                className="h-5 w-5 text-gray-400" 
              />
            </div>
          )}
          
          {(prefix || startAdornment) && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              {(() => {
                const content = prefix || startAdornment;
                return typeof content === 'string' ? (
                  <span className="text-gray-500 dark:text-gray-400">{content}</span>
                ) : (
                  content
                );
              })()}
            </div>
          )}
          
          <input
            ref={ref}
            className={inputClasses}
            disabled={disabled}
            {...inputProps}
          />
          
          {(suffix || endAdornment) && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {(() => {
                const content = suffix || endAdornment;
                return typeof content === 'string' ? (
                  <span className="text-gray-500 dark:text-gray-400">{content}</span>
                ) : (
                  content
                );
              })()}
            </div>
          )}
          
          {iconEnd && (
            <div 
              className={classNames(
                "absolute inset-y-0 right-0 flex items-center pr-3", 
                { "cursor-pointer": !!onIconEndClick }
              )}
              onClick={onIconEndClick}
            >
              <IconSVG 
                icon={iconEnd} 
                className="h-5 w-5 text-gray-400" 
              />
            </div>
          )}
        </div>
        
        {(error || helperText) && (
          <p 
            className={classNames("mt-1 text-sm", {
              "text-red-600 dark:text-red-500": error,
              "text-gray-500 dark:text-gray-400": !error && helperText
            })}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;